import { useCallback, useEffect, useMemo, useState } from "react";
import { message, Modal } from "antd";

// Ajusta esta ruta a la tuya:
import {
  getDeviceThresholds,
  saveThreshold,
  deleteThreshold,
} from "../services/thresholdService";

const { confirm } = Modal;

export default function useThresholds(deviceId) {
  const [thresholds, setThresholds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingThreshold, setEditingThreshold] = useState(null);

  const load = useCallback(async () => {
    if (!deviceId) {
      setThresholds([]);
      return;
    }
    setLoading(true);
    try {
      const res = await getDeviceThresholds(deviceId);
      if (res?.success) setThresholds(res.data || []);
      else message.error(res?.error || "Error loading thresholds");
    } catch {
      message.error("Error loading thresholds");
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    load();
  }, [load]);

  const keyOf = (t) => `${t.elementId}-${t.periodicityId}`;

  const existingKeys = useMemo(() => {
    const s = new Set();
    for (const t of thresholds) s.add(keyOf(t));
    return s;
  }, [thresholds]);

  const save = useCallback(
    async (values) => {
      if (!deviceId) return false;

      const elementId = Number(values.elementId);
      const periodicityId = Number(values.periodicityId);

      // Evita duplicado (salvo si estás editando justo ese mismo registro)
      const currentKey = `${elementId}-${periodicityId}`;
      const editingKey = editingThreshold ? keyOf(editingThreshold) : null;

      if (existingKeys.has(currentKey) && currentKey !== editingKey) {
        message.warning("This element + periodicity already has a threshold.");
        return false;
      }

      try {
        const res = await saveThreshold(deviceId, editingThreshold?.id, {
          ...values,
          elementId,
          periodicityId,
        });

        if (res?.success) {
          message.success("Threshold saved");
          setEditingThreshold(null);
          await load();
          return true;
        }
        message.error(res?.error || "Error saving threshold");
        return false;
      } catch {
        message.error("Error saving threshold");
        return false;
      }
    },
    [deviceId, editingThreshold, existingKeys, load]
  );

  const remove = useCallback(
    (threshold) => {
      confirm({
        title: "Delete threshold?",
        content: "This action cannot be undone.",
        okType: "danger",
        onOk: async () => {
          try {
            const res = await deleteThreshold(threshold.id);
            if (res?.success) {
              message.success("Threshold deleted");
              await load();
            } else {
              message.error(res?.error || "Error deleting threshold");
            }
          } catch {
            message.error("Error deleting threshold");
          }
        },
      });
    },
    [load]
  );

  const startCreate = useCallback(() => {
    setEditingThreshold(null);
  }, []);

  const startEdit = useCallback((t) => {
    setEditingThreshold(t);
  }, []);

  return {
    thresholds,
    loading,
    editingThreshold,
    setEditingThreshold, // por si lo necesitas desde UI
    startCreate,
    startEdit,
    save,
    remove,
    reload: load,
  };
}
